import React from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { useFormat } from '../../context/FormatContext';

const { FiArrowRight, FiMaximize, FiType } = FiIcons;

const TRIM_SIZES = [
  { id: '5x8', name: '5" x 8"', desc: 'Pocket / Small Fiction', aspect: 'aspect-[5/8]' },
  { id: '5.5x8.5', name: '5.5" x 8.5"', desc: 'Digest / Standard Fiction', aspect: 'aspect-[5.5/8.5]' },
  { id: '6x9', name: '6" x 9"', desc: 'US Trade / Non-fiction', aspect: 'aspect-[6/9]' }
];

const FONT_SIZES = [
  { id: '10pt', name: '10 pt', desc: 'Compact (Saves pages)' },
  { id: '11pt', name: '11 pt', desc: 'Standard (Recommended)' },
  { id: '12pt', name: '12 pt', desc: 'Large / Easy Read' }
];

const PrintConfigStep = () => {
  const { pipelineState, updatePrintConfig, proceedToExport } = useFormat();
  const { printConfig } = pipelineState;

  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="max-w-4xl mx-auto py-8">
      <div className="text-center mb-10">
        <h2 className="text-3xl font-serif text-slate-800 mb-3">Print Configuration</h2>
        <p className="text-slate-500">Configure layout dimensions for Amazon KDP Paperback (Print-on-Demand).</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
        
        {/* Trim Size Selection */}
        <div className="bg-white p-8 rounded-2xl border border-slate-200 shadow-sm">
          <div className="flex items-center space-x-3 mb-6">
            <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center text-primary">
              <SafeIcon icon={FiMaximize} className="text-xl" />
            </div>
            <div>
              <h3 className="font-bold text-slate-800 text-lg">Book Trim Size</h3>
              <p className="text-xs text-slate-500">Select the physical dimensions of your book.</p>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-4">
            {TRIM_SIZES.map(size => (
              <button
                key={size.id}
                onClick={() => updatePrintConfig('trimSize', size.id)}
                className={`flex flex-col items-center justify-end p-4 rounded-xl border-2 transition-all ${
                  printConfig.trimSize === size.id 
                    ? 'border-primary bg-primary/5 shadow-md' 
                    : 'border-slate-200 hover:border-primary/30 bg-white'
                }`}
              >
                <div className={`w-16 bg-white border shadow-sm mb-4 transition-all ${size.aspect} ${
                  printConfig.trimSize === size.id ? 'border-primary border-2' : 'border-slate-300'
                }`}></div>
                <span className="font-bold text-slate-800 text-sm mb-1">{size.name}</span>
                <span className="text-[10px] text-slate-500 font-medium leading-tight text-center">{size.desc}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Font Size Selection */}
        <div className="bg-white p-8 rounded-2xl border border-slate-200 shadow-sm">
          <div className="flex items-center space-x-3 mb-6">
            <div className="w-10 h-10 rounded-xl bg-secondary/10 flex items-center justify-center text-secondary">
              <SafeIcon icon={FiType} className="text-xl" />
            </div>
            <div>
              <h3 className="font-bold text-slate-800 text-lg">Base Font Size</h3>
              <p className="text-xs text-slate-500">Determines the body text readability and page count.</p>
            </div>
          </div>

          <div className="space-y-3">
            {FONT_SIZES.map(font => (
              <button
                key={font.id}
                onClick={() => updatePrintConfig('fontSize', font.id)}
                className={`w-full flex items-center justify-between p-4 rounded-xl border-2 transition-all ${
                  printConfig.fontSize === font.id 
                    ? 'border-secondary bg-secondary/5 shadow-md' 
                    : 'border-slate-200 hover:border-secondary/30 bg-white'
                }`}
              >
                <div className="flex items-center space-x-4">
                  <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                    printConfig.fontSize === font.id ? 'border-secondary bg-secondary' : 'border-slate-300 bg-white'
                  }`}>
                    {printConfig.fontSize === font.id && <div className="w-2 h-2 rounded-full bg-white" />}
                  </div>
                  <span className="font-bold text-slate-800 text-base">{font.name}</span>
                </div>
                <span className="text-xs text-slate-500 font-medium">{font.desc}</span>
              </button>
            ))}
          </div>

          <div className="mt-6 p-4 bg-slate-50 rounded-xl border border-slate-100">
            <p className="text-xs text-slate-500 text-center leading-relaxed">
              <strong>Note:</strong> Pagination, margins, headers, and footers will be automatically calculated based on these dimensions when generating the print-ready PDF.
            </p>
          </div>
        </div>

      </div>

      <div className="flex justify-end">
        <button 
          onClick={proceedToExport}
          className="bg-primary hover:bg-blue-800 text-white px-8 py-4 rounded-xl font-bold uppercase tracking-widest text-sm transition-all shadow-lg flex items-center"
        >
          <span>Select Export Style</span>
          <SafeIcon icon={FiArrowRight} className="ml-2" />
        </button>
      </div>

    </motion.div>
  );
};

export default PrintConfigStep;